import React, { useCallback } from "react";
import { Settings } from "@mui/icons-material";
import { Alert, AlertTitle, Button, Paper } from "@mui/material";

import { useGetVersionQuery } from "fond/api";
import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { useGetPlannerLayers } from "fond/project/panels/ExtendedDesign/hooks/useGetPlannerLayers";
import { loadProject } from "fond/project/redux";
import { Project } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { LinearProgressWithLabel, StackedNavigationButton, useStackedNavigationContext } from "fond/widgets";

interface SettingsPanelProps {
  project: Project;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({ project }: SettingsPanelProps) => {
  const dispatch = useAppDispatch();
  const { open } = useStackedNavigationContext();
  const versionId = useAppSelector((state) => state.project.versionId);
  const { data: version } = useGetVersionQuery(versionId ?? "", { skip: !versionId });
  const { layerKeys } = useGetPlannerLayers(versionId);

  const addressLayer = layerKeys.includes("input_layers/demand");
  const undergroundLayer = layerKeys.includes("input_layers/streets");

  const handleOnComplete = useCallback(async () => {
    // TODO: we should probably go to the 'Design' step automagically once this finishes?
    await dispatch(loadProject({ uuid: project.ID, forceRefetch: true }));
  }, [dispatch, project.ID]);

  const { workflowState, workflowProgress, runWorkflow } = useCustomWorkflow({
    versionId,
    key: "planner_extended_data_prep",
    onComplete: handleOnComplete,
  });

  const isDataPrepping = workflowState === "STARTING" || workflowState === "IN_PROGRESS";
  const canDataPrep =
    addressLayer && undergroundLayer && Boolean(runWorkflow) && !isDataPrepping && Boolean(version?.Architecture?.DataPrepConfiguration);

  const handleOnClick = () => {
    open("dataPrepSettings");
  };

  const handleDataPrep = async () => {
    if (!runWorkflow) {
      return;
    }
    runWorkflow();
  };

  return (
    <>
      <StackedNavigationButton
        data-testid="data-prep-settings-navigation"
        icon={<Settings />}
        title="Settings"
        onClick={handleOnClick}
        disabled={isDataPrepping}
        sx={{ m: 0 }}
      />
      <Button fullWidth variant="contained" size="small" sx={{ mt: 1 }} disabled={!canDataPrep} onClick={handleDataPrep}>
        Generate
      </Button>
      {isDataPrepping && (
        <Paper variant="outlined" sx={{ py: 2, px: 2.75, mt: 2 }}>
          <LinearProgressWithLabel
            color="primary"
            value={(workflowProgress ?? 0) * 100}
            progressBarMarginRight={0}
            title="Generating"
            subtext="Processing..."
          />
        </Paper>
      )}
      {workflowState === "ERROR" && (
        <Alert severity="error" sx={{ mt: 4 }}>
          <AlertTitle>Generation failed</AlertTitle>
          Data could not be generated.
        </Alert>
      )}
    </>
  );
};

export default SettingsPanel;
